import type { ImageType } from '@vizit/api';
import React from 'react';
import { ClearableSelect } from '../clearable-select';

/**
 * Enum representing the available asset types
 */
enum AssetType {
  All = 'all',
  Hero = 'hero',
  Carousel = 'carousel',
}

interface AssetTypeSelectProps {
  /**
   * The selected asset type
   * @optional
   * @type {AssetType}
   * @default AssetType.All
   */
  assetType?: AssetType | ImageType | null;
  /**
   * The function to call when the asset type changes
   * @required
   * @type {(assetType: AssetType | ImageType | null) => void}
   */
  onAssetTypeChange: (assetType: ImageType | null) => void;
}

const ASSET_TYPE_ITEMS = [
  { id: AssetType.Hero, label: 'Hero' },
  { id: AssetType.Carousel, label: 'Carousel' },
];

/**
 * Select component for choosing an asset type filter.
 * Allows users to filter assets by their type (Hero, Carousel, or All).
 *
 * @param props - The component props
 * @param props.assetType - The currently selected asset type
 * @param props.onAssetTypeChange - Callback fired when the asset type changes
 */
function AssetTypeSelect(props: AssetTypeSelectProps) {
  const { assetType = AssetType.All, onAssetTypeChange } = props;

  const selectedKey = React.useMemo(
    () => (assetType && assetType !== AssetType.All ? (assetType as string) : null),
    [assetType],
  );

  const handleSelectionChange = (key: string | null) => {
    onAssetTypeChange(key as ImageType | null);
  };

  return (
    <ClearableSelect
      ariaLabel="Asset type"
      placeholder="All types"
      items={ASSET_TYPE_ITEMS}
      selectedKey={selectedKey}
      onSelectionChange={handleSelectionChange}
    />
  );
}

export { AssetTypeSelect, AssetType };
export type { AssetTypeSelectProps };
